import { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Select, { GroupBase, OptionsOrGroups, PropsValue } from "react-select";
import { useSearchParams } from "react-router-dom";

import HubInputField from "../../components/inputType";
import FooterText from "../../components/footerText";
import AuthSubHeader from "../../components/authSubHeader";

import useFetch from "../../hooks/useFetch";
import { ToastContext } from "../../context/toastContext";
import { AuthContext } from "../../context/authContext";
import { LoaderContext } from "../../context/loaderContext";

import { AddProviderSchema } from "../../validation/addProvider";
import { APIEndpoints } from "../../types/apiTypes";
import { ToastTypes } from "../../types";
import { AddProviderForm, AddProviderResponse, NPILookupResponse, NPISpeciality } from "./types";
import { useDebouncedCallback } from "use-debounce";
import { PracticeDropdownResponse } from "src/components/caseStepTwo/types";




const AddProvider: FC = (): JSX.Element => {
  const [preservedNpi, updatePreservedNpi] = useState<string>("");

  const [ searchParams ] = useSearchParams()
  const { showToast } = useContext(ToastContext);
  const { userData } = useContext(AuthContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);

  const {
    control,
    handleSubmit,
    formState,
    getValues,
    setValue,
    trigger,
    reset: resetForm
  } = useForm<AddProviderForm>({
    mode: 'all',
    resolver: yupResolver(AddProviderSchema(searchParams.get("title"))),
  });



  const { data: specialityData } = useFetch<NPISpeciality[]>(APIEndpoints.GET_PROVIDER_SPECIALITIES);




  const { mutate: lookupNpi, error: npiLookupError, data: npiLookupResponse, removeErrorAndData: clearNpiResponse } =
    useFetch<NPILookupResponse>( APIEndpoints.NPI_LOOKUP );

  /**
   * Checking for NPILookup call errors
   * @NPILookUpResponseHandler
   */
  useEffect( () => {
    if (npiLookupError)
      hideLoader();

    if (npiLookupResponse) {
      if (npiLookupResponse.doctor_data) {
        hideLoader();
        const { basic, taxonomy_data } = npiLookupResponse.doctor_data;
        setValue("ref_taxonomy_id", taxonomy_data.code);
        setValue("sln", taxonomy_data.license || "");
        setValue("firstname", basic.first_name);
        setValue("lastname", basic.last_name);
        setValue("tax_id", basic.tax_id || "");
        trigger();
        showToast(["Doctor data found"], ToastTypes.INFO);
      } else {
        hideLoader();
        setValue("ref_taxonomy_id", "");
        setValue("sln", "");
        setValue("firstname", "");
        setValue("lastname", "");
        setValue("tax_id", "");
        showToast([`No Data Found`], ToastTypes.ERROR);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npiLookupResponse, npiLookupError]);

  /**
   * When we enter after entering NPI in the input field
   * @param event
   */
  const callNPILookup = () => {
    const npi = getValues("npi");

    if (!npi || ( npi && npi.length === 0 ) || formState.errors.npi)
      showToast([`Please enter valid NPI for lookup`], ToastTypes.ERROR);
    else {
      if (preservedNpi !== npi) {
        updatePreservedNpi(npi || "");
        clearNpiResponse();
        setLoader();
        const data = {
          'global_doctor[npi]': npi,
        };
        lookupNpi( null, null, data );
      }
    }
  };




  /**
   * Submitting form and getting response to add new provider/doctor
   * @First Adding hook for api call
   * @Second Error handling for api call
   * @Third Success handling for api call
   * @Fourth Form Submit Handler which will ultimately call the mutate function to call api
   */
  const {
    loading: addProviderLoading,
    data: addProviderResponse,
    mutate: callAddProvider,
    removeErrorAndData: refreshAddProviderRequest
  } = useFetch<AddProviderResponse>(APIEndpoints.ADD_PROVIDER);

  /**
   * @Third
   */
  useEffect(() => {
    if ( addProviderResponse ) {
      const { status: { code, message } } = addProviderResponse;
      if (code === 200) {
        if (message.includes("already exists"))
          showToast([message], ToastTypes.ERROR)
        else
          showToast(['Provider Saved Successfully'], ToastTypes.SUCCESS);
        resetForm();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addProviderResponse]);

  /**
   * @Fourth
   */
  const submitHandler = async (values: AddProviderForm) => {
    refreshAddProviderRequest();

    if (userData) {
      const { id } = userData;
      const title = searchParams.get("title")
      let doctor_type;
      if (title) {
        if (title.includes("Referring")) doctor_type = "referring"
        if (title.includes("Treating")) doctor_type = "treating"
      }

      const res = {
        user_id: id,
        ...(doctor_type ? { doctor_type } : {}),
        global_doctor: {
          ...values,
          ref_taxonomy_id: ( specialityData?.find((speciality) => speciality.code === values.ref_taxonomy_id)?.id || 0).toString(),
        },
      };
      callAddProvider(res);
    }
  };




  const { loading: practiceAPILoader, data: practiceListing, mutate: getPracticeListing } = 
    useFetch<PracticeDropdownResponse>(APIEndpoints.PRACTICE_DROPDOWN, {}, {}, false)

  const getPracticeListingBySearch = (searchQuery: string) => {
    if (searchQuery && searchQuery.length >= 3)
      getPracticeListing(null, null, { search_query: searchQuery })
  }

  const getDebouncedPracticeListing = useDebouncedCallback(getPracticeListingBySearch, 1500)





  return (
      <div className="overflow-y-auto overflow-x-hidden d-flex flex-column flex-fill">
        <div className="bg-mist py-2">
          <div className="container-fluid">
            <AuthSubHeader/>
          </div>
        </div>
        <div className="container-fluid pt-2 d-flex flex-column flex-fill">
          <div className="mt-4">
            <h2 className="m-0 page-heading">
              {searchParams.get("title") ? searchParams.get("title") : "Add Doctor"}
            </h2>
            <p>
              Please input the information for the provider you wish to create below. The provider will
              be automatically added to all practices attached to your account, and available immediately for use in
              new requests.
            </p>
            <div className="card">
              <div className="card-body">
                <form onSubmit={handleSubmit(submitHandler)}>
                  {
                    searchParams.get("title") ?
                        <div className="row mb-3">
                          <label htmlFor="practice_id" className="col-md-2 col-12 form-label">Practice</label>
                          <div className="col-md-10 col-12">
                            <Controller
                                name="practice_id"
                                control={control}
                                defaultValue={0}
                                render={({field}) => (
                                    <Select
                                        options={
                                          (
                                              practiceListing && practiceListing.data && practiceListing.data.length > 0 ?
                                                  practiceListing.data.map(
                                                      (prac) =>
                                                          ({
                                                            value: prac.id,
                                                            label: `${prac.name}${prac.address && prac.address ? ` - ${prac.address.full_address}` : ""}`
                                                          })
                                                  )
                                                  : []
                                          ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                                        }
                                        isSearchable={true}
                                        id="practice_id"
                                        placeholder="Enter Practice Name..."
                                        noOptionsMessage={() => practiceAPILoader ? "Loading..." : "Please enter 3 or more characters..."}
                                        {...field}
                                        value={(() => {
                                          if (field.value === 0) return null;
                                          if (practiceListing) {
                                            const data = practiceListing.data.find((option) => {
                                              return option.id === field.value;
                                            });
                                            if (data)
                                              return {
                                                value: data.id,
                                                label: `${data.name}${data.address && data.address ? ` - ${data.address.full_address}` : ""}`
                                              } as unknown as PropsValue<string>;
                                          }
                                        })()}
                                        onChange={(value) => {
                                          field.onChange((value as unknown as { value: string; label: string; }).value)
                                        }}
                                        onInputChange={getDebouncedPracticeListing}
                                    />
                                )}
                            />
                          </div>
                        </div>
                        : <></>
                  }

                  <div className="row">
                    <div className="col-md-6 col-12">
                      <Controller
                          name="npi"
                          control={control}
                          defaultValue=""
                          render={({field, fieldState: {error}}) => (
                              <div className={"row"}>
                                <label className={"form-label col-lg-4 col-md-4 col-12 p-2"}>NPI</label>
                                <div className={"col-lg-8 col-md-8 col-12"}>
                                  <HubInputField
                                      inputId="npi"
                                      isRowBased={false}
                                      isRequired={true}
                                      field={field}
                                      error={error}
                                      inputPlaceholder="NPI"
                                      onBlur={callNPILookup}
                                      onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                          event.preventDefault();
                                          callNPILookup();
                                        }
                                      }}
                                  />
                                </div>
                              </div>
                          )}
                      />
                    </div>

                    <div className="col-md-6 col-12">
                      <Controller
                          name="ref_taxonomy_id"
                          control={control}
                          defaultValue=""
                          render={({field, fieldState: {error}}) => (
                              <div className="row">
                                <label className={"form-label col-lg-4 col-md-4 col-12 p-2"}>Spec.</label>

                                <div className={"col-lg-8 col-md-8 col-12"}>
                                  <HubInputField
                                      inputId="ref_taxonomy_id"
                                      isRowBased={false}
                                      isRequired={true}
                                      field={field}
                                      error={error}
                                      // inputLabel="Spec."
                                      inputPlaceholder="Select a Taxonomy..."
                                      isDropdown={true}
                                      isSearchable={true}
                                      dropdownData={
                                        specialityData ?
                                            specialityData.map(
                                                (speciality) => ({
                                                  label: speciality.name,
                                                  value: speciality.code
                                                })
                                            ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                                            : []
                                      }
                                  />
                                </div>
                              </div>
                          )}
                      />
                    </div>

                    <div className="col-md-6 col-12">
                      <Controller
                          name="firstname"
                          control={control}
                          defaultValue=""
                          render={({field, fieldState: {error}}) => (
                              <div className={"row"}>
                                <label className={"form-label col-lg-4 col-md-4 col-12 p-2"}>First
                                  Name</label>
                                <div className={"col-lg-8 col-md-8 col-12"}>
                                  <HubInputField
                                      inputId="firstname"
                                      isRowBased={false}
                                      isRequired={true}
                                      field={field}
                                      error={error}
                                      // inputLabel="First Name"
                                      inputPlaceholder="First Name"
                                  />
                                </div>
                              </div>
                          )}
                      />
                    </div>

                    <div className="col-md-6 col-12">
                      <Controller
                          name="lastname"
                          control={control}
                          defaultValue=""
                          render={({field, fieldState: {error}}) => (
                              <div className={"row"}>
                                <label className={"form-label col-lg-4 col-md-4 col-12 p-2"}>Last
                                  Name</label>
                                <div className={"col-lg-8 col-md-8 col-12"}>
                                  <HubInputField
                                      inputId="lastname"
                                      isRowBased={false}
                                      isRequired={true}
                                      field={field}
                                      error={error}
                                      // inputLabel="Last Name"
                                      inputPlaceholder="Last Name"
                                  />
                                </div>
                              </div>
                          )}
                      />
                    </div>

                    <div className="col-md-6 col-12">
                      <Controller
                          name="dea"
                          control={control}
                          defaultValue=""
                          render={({field, fieldState: {error}}) => (
                              <div className={"row"}>
                                <label
                                    className={"form-label col-lg-4 col-md-4 col-12 p-2"}>DEA</label>
                                <div className={"col-lg-8 col-md-8 col-12"}>
                                  <HubInputField
                                      inputId="dea"
                                      isRowBased={false}
                                      isRequired={false}
                                      field={field}
                                      error={error}
                                      // inputLabel="DEA"
                                      inputPlaceholder="DEA Number"
                                  />
                                </div>
                              </div>
                          )}
                      />
                    </div>

                    <div className="col-md-6 col-12">
                      <Controller
                          name="sln"
                          control={control}
                          defaultValue=""
                          render={({field, fieldState: {error}}) => (
                              <div className={"row"}>
                                <label
                                    className={"form-label col-lg-4 col-md-4 col-12 p-2"}>SLN</label>
                                <div className={"col-lg-8 col-md-8 col-12"}>
                                  <HubInputField
                                      inputId="sln"
                                      isRowBased={false}
                                      isRequired={false}
                                      field={field}
                                      error={error}
                                      // inputLabel="SLN"
                                      inputPlaceholder="State License Number"
                                  />
                                </div>
                              </div>
                          )}
                      />
                    </div>

                    <div className="col-md-6 col-12">
                      <Controller
                          name="tax_id"
                          control={control}
                          defaultValue=""
                          render={({
                                     field,
                                     fieldState: {error}
                                   }) => (
                              <div className={"row"}>
                                <label
                                    className={"form-label col-lg-4 col-md-4 col-12 p-2"}>Tax
                                  ID</label>
                                <div className={"col-lg-8 col-md-8 col-12"}>
                                  <HubInputField
                                      inputId="tax_id"
                                      isRowBased={false}
                                      isRequired={false}
                                      field={field}
                                      error={error}
                                      // inputLabel="Tax ID"
                                      inputPlaceholder="Tax ID"
                                  />
                                </div>
                              </div>
                          )}
                      />
                    </div>

                    <div className="col-md-6 col-12">
                      <Controller
                          name="ptan"
                          control={control}
                          defaultValue=""
                          render={({
                                     field,
                                     fieldState: {error}
                                   }) => (
                              <div className={"row"}>
                                <label
                                    className={"form-label col-lg-4 col-md-4 col-12 p-2"}>PTAN</label>
                                <div className={"col-lg-8 col-md-8 col-12"}>
                                  <HubInputField
                                      inputId="ptan"
                                      isRowBased={false}
                                      isRequired={false}
                                      field={field}
                                      error={error}
                                      // inputLabel="PTAN"
                                      inputPlaceholder="Provider Transaction Access Number"
                                  />
                                </div>
                              </div>
                          )}
                      />
                    </div>
                  </div>

                  <button
                      type="submit" disabled={addProviderLoading}
                      className="btn btn-primary"
                  >
                    {addProviderLoading && (
                        <i className="text-white me-2 spinner-border"
                           style={{width: `24px`, height: `24px`, fontSize: "10px"}}></i>
                    )}
                    Add Provider
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <FooterText footerType="black"/>
      </div>
  );
};

export default AddProvider;
